import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import Animated, {
  SharedValue,
  useAnimatedStyle,
} from 'react-native-reanimated';
import { Image, ImageLoadEventData } from 'expo-image';
import { Box } from '@/components/gluestack/box';
import { Spinner } from '@/components/gluestack/spinner';
import { hasUriDisplayed, markUriDisplayed, setCachedDimensions } from './imageDimensionsCache';
import type { IPaneSlot } from './usePaneSlots';

const AnimatedImage = Animated.createAnimatedComponent(Image);

interface IImageViewerPaneProps {
  uri: string | null;
  paneWidth: number;
  paneHeight: number;
  isDesktop: boolean;
  // Per-pane zoom/pan SVs (stable across re-renders).
  paneSlot: IPaneSlot;
  // Whether this pane is currently the active (centered) one. Used to show
  // grab-cursor styling on web while panning a zoomed image.
  isActive: boolean;
  isPanning: SharedValue<boolean>;
  allowImageDownload?: boolean;
  onLoadError?: (uri: string, message?: string) => void;
  // Fires when the pane's image settles (load success or error). The viewer
  // uses this to defer neighbor prefetch until the visible photo has painted,
  // so speculative prefetch never competes with the photo the user is viewing.
  onLoadEnd?: (uri: string) => void;
}

export const ImageViewerPane: React.FC<IImageViewerPaneProps> = ({
  uri,
  paneWidth,
  paneHeight,
  isDesktop,
  paneSlot,
  isActive,
  isPanning,
  allowImageDownload = false,
  onLoadError,
  onLoadEnd,
}) => {
  const [imageLoading, setImageLoading] = useState(false);
  const showSpinnerTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isLoadedRef = useRef(false);
  const currentUriRef = useRef(uri);

  const clearSpinnerTimeout = useCallback(() => {
    if (showSpinnerTimeoutRef.current) {
      clearTimeout(showSpinnerTimeoutRef.current);
      showSpinnerTimeoutRef.current = null;
    }
  }, []);

  // Show the spinner only for a fresh image that takes long enough to load.
  // A photo this viewer has already painted in a pane (tracked via
  // hasUriDisplayed, set from the pane's own onLoad) is treated as loaded, so
  // recycling/re-entering a pane never flashes a spinner over an image that's
  // already on screen. We deliberately do NOT key off the dimensions cache:
  // it's also populated by Image.getSize() and the morph overlay on open, which
  // would make the just-tapped photo look "loaded" and suppress its spinner.
  // Once shown, the spinner stays up until onLoad/onError fires — on a slow
  // connection that means a visible spinner instead of a blank screen, never
  // one force-hidden mid-load.
  useEffect(() => {
    currentUriRef.current = uri;
    clearSpinnerTimeout();
    setImageLoading(false);

    if (!uri) {
      isLoadedRef.current = false;
      return;
    }

    const alreadyLoaded = hasUriDisplayed(uri);
    isLoadedRef.current = alreadyLoaded;
    if (alreadyLoaded) return;

    showSpinnerTimeoutRef.current = setTimeout(() => {
      if (!isLoadedRef.current && currentUriRef.current === uri) {
        setImageLoading(true);
      }
      showSpinnerTimeoutRef.current = null;
    }, 150);

    return () => {
      clearSpinnerTimeout();
    };
  }, [uri, clearSpinnerTimeout]);

  const finishLoading = useCallback(() => {
    isLoadedRef.current = true;
    clearSpinnerTimeout();
    setImageLoading(false);
    if (uri) onLoadEnd?.(uri);
  }, [clearSpinnerTimeout, uri, onLoadEnd]);

  const handleImageLoad = useCallback((event: ImageLoadEventData) => {
    // Mark displayed BEFORE finishLoading() — finishLoading fires onLoadEnd, and
    // any consumer reached from it should already see this URI as displayed.
    if (uri) markUriDisplayed(uri);
    finishLoading();
    const source = event?.source;
    if (uri && source?.width && source?.height) {
      setCachedDimensions(uri, source.width, source.height);
    }
  }, [finishLoading, uri]);

  const handleImageError = useCallback((event: any) => {
    finishLoading();
    if (uri) {
      onLoadError?.(uri, event?.error ?? event?.nativeEvent?.error);
    }
  }, [finishLoading, onLoadError, uri]);

  const animatedStyle = useAnimatedStyle(() => {
    const style: any = {
      transform: [
        { scale: paneSlot.scale.value },
        { translateX: paneSlot.translateX.value },
        { translateY: paneSlot.translateY.value },
      ],
    };

    if (isDesktop && isActive) {
      if (paneSlot.scale.value > 1) {
        style.cursor = isPanning.value ? 'grabbing' : 'zoom-out';
      } else {
        style.cursor = 'zoom-in';
      }
    }

    if (Platform.OS === 'web') {
      style.willChange = 'transform';
      style.backfaceVisibility = 'hidden';
    }

    return style;
  });

  return (
    <View
      style={{ width: paneWidth, height: paneHeight }}
      pointerEvents="box-none"
    >
      {uri ? (
        <View
          style={StyleSheet.absoluteFill}
          pointerEvents="box-none"
          className={
            allowImageDownload
              ? 'web:[&_img]:pointer-events-auto web:[&_img]:select-none web:[&_img]:[-webkit-user-drag:none]'
              : 'web:[&_img]:pointer-events-none'
          }
        >
          <AnimatedImage
            source={{ uri }}
            // Reset the view when the slot recycles to a different photo.
            // Without this, expo-image keeps the previously-loaded photo on
            // screen until the new URI finishes downloading — on a slow
            // connection that stale photo lingers, so a slot rotating to a new
            // index shows an already-seen photo (looks like the carousel
            // repeating/reordering while swiping). Keyed on uri so the centered
            // pane — whose uri is stable across a commit — never resets.
            recyclingKey={uri}
            contentFit="contain"
            style={[StyleSheet.absoluteFill, animatedStyle]}
            onLoad={handleImageLoad}
            onError={handleImageError}
            {...(Platform.OS === 'web'
              ? ({
                  draggable: false,
                  onDragStart: (event: React.DragEvent) => event.preventDefault(),
                } as any)
              : {})}
          />
        </View>
      ) : null}
      {imageLoading && (
        <Box className="absolute inset-0 z-10 flex items-center justify-center">
          <Spinner size="large" color="#fff" />
        </Box>
      )}
    </View>
  );
};
